import { Component, EventEmitter, Input, Output, HostListener } from '@angular/core';

export interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  type?:
  | 'text'
  | 'name'
  | 'email'
  | 'phone'
  | 'badge'
  | 'status'
  | 'number'
  | 'date'
  | 'actions'
  | 'hierarchy'
  | 'roleCell'
  | 'tags'
  | 'linkedUsers';
  /**
   * For 'badge' columns: forces every row to use this single modifier class
   * (`dt-badge--{badgeColor}`) instead of one derived per-value. Use this when
   * the badge's values are open-ended/user-defined (so a fixed color palette
   * per value doesn't make sense).
   */
  badgeColor?: string;
  /** For 'text' / 'date' columns: renders `row[subKey]` as a muted second line under the value. */
  subKey?: string;
}

export interface TableAction {
  label: string;
  icon: string;
  action: (row: TableRow) => void;
  /** Optional — when provided, the action only renders for rows where this returns true */
  visible?: (row: TableRow) => boolean;
}

export interface TableRow {
  id: string;
  [key: string]: any;
}

export type AvatarTypes = 'initials' | 'icon' | 'none';

@Component({
  selector: 'app-data-table',
  standalone: false,
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
})
export class DataTableComponent {
  @Input() columns: TableColumn[] = [];
  @Input() rows: TableRow[] = [];
  @Input() totalResults: number = 0;
  @Input() currentPage: number = 1;
  @Input() itemsPerPage: number = 12;
  @Input() showCheckboxes: boolean = true;
  @Input() showPagination: boolean = true;
  @Input() avatarType: AvatarTypes = 'initials';
  @Input() iconPath?: string;
  @Input() resultsLabel: string = 'users';
  @Input() serverSidePagination: boolean = false;
  @Input() iconUrl?: string;
  @Input() actions?: TableAction[] = [];
  @Input() showSubText?: boolean = false
  
  activeDropdownRowId: string | null = null;

  @Output() pageChange = new EventEmitter<number>();
  @Output() itemsPerPageChange = new EventEmitter<number>();
  @Output() rowClick = new EventEmitter<TableRow>();

  selectedRows = new Set<string>();
  selectAll = false;
  itemsPerPageOptions = [10, 20, 50, 100];

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalResults / this.itemsPerPage));
  }

  get startItem(): number {
    if (this.totalResults === 0) return 0;
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get endItem(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalResults);
  }

  get paginatedRows(): TableRow[] {
    if (this.serverSidePagination) {
      return this.rows;
    }
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.rows.slice(start, end);
  }

  get selectedRowsArray(): TableRow[] {
    return this.rows.filter((row) => this.selectedRows.has(row.id));
  }

  onSelectAll(): void {
    this.selectAll = !this.selectAll;

    if (this.selectAll) {
      this.paginatedRows.forEach((row) => this.selectedRows.add(row.id));
    } else {
      this.paginatedRows.forEach((row) => this.selectedRows.delete(row.id));
    }
  }

  onSelectRow(rowId: string): void {
    if (this.selectedRows.has(rowId)) {
      this.selectedRows.delete(rowId);
    } else {
      this.selectedRows.add(rowId);
    }

    this.selectAll = this.paginatedRows.length > 0 &&
      this.paginatedRows.every((row) => this.selectedRows.has(row.id));
  }

  isRowSelected(rowId: string): boolean {
    return this.selectedRows.has(rowId);
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.selectAll = false;
      this.pageChange.emit(page);
    }
  }

  onItemsPerPageChange(): void {
    this.selectAll = false;
    this.itemsPerPageChange.emit(this.itemsPerPage);
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;

    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  getCellValue(row: TableRow, column: TableColumn): any {
    return row[column.key];
  }

  getColumnType(column: TableColumn): string {
    return column.type ?? 'text';
  }

  getBadgeClass(column: TableColumn, value: string, row?: TableRow): string {
    // Per-row override: `<columnKey>BadgeColor` on the row lets a column display
    // one value (e.g. "92%") while coloring the badge from an independently
    // computed band (e.g. "high"/"medium"/"low"). Falls through to the existing
    // column-level/derived-from-value behavior when absent, so no existing
    // 'badge' column is affected.
    const rowOverride = row?.[`${column.key}BadgeColor`];
    if (rowOverride) {
      return `dt-badge dt-badge--${rowOverride}`;
    }
    if (column.badgeColor) {
      return `dt-badge dt-badge--${column.badgeColor}`;
    }
    const normalized = (value || '').toLowerCase().replace(/\s+/g, '-');
    return `dt-badge dt-badge--${normalized}`;
  }

  getStatusClass(value: string): string {
    const normalized = (value || '').toLowerCase().replace(/\s+/g, '-');
    return `status status--${normalized}`;
  }

  formatNumber(value: any): string {
    if (value === null || value === undefined || value === '') return '-';
    return new Intl.NumberFormat('en-US').format(Number(value));
  }

  toggleDropdown(rowId: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.activeDropdownRowId === rowId) {
      this.activeDropdownRowId = null;
    } else {
      this.activeDropdownRowId = rowId;
    }
  }

  onActionClick(action: TableAction, row: TableRow, event: MouseEvent): void {
    event.stopPropagation();
    this.activeDropdownRowId = null;
    if (action.action) {
      action.action(row);
    }
  }

  isIconUrl(icon: string): boolean {
    return icon.includes('/') || icon.includes('.') || icon.startsWith('assets/');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.actions-wrapper')) {
      this.activeDropdownRowId = null;
    }
  }

  /** Returns only the actions whose optional visible() predicate passes for this row */
  getVisibleActions(row: TableRow): TableAction[] {
    return (this.actions ?? []).filter(a => !a.visible || a.visible(row));
  }
}